import {ChecklistIcon, FileIcon, KebabHorizontalIcon, LinkExternalIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Link} from '@primer/react'
import {memo, useCallback} from 'react'

import type {PanelArtifact} from './ArtifactPanelContent'
import styles from './ArtifactRow.module.css'
import {safeHref} from './safe-href'
import type {IssueArtifact} from './types'

function ArtifactTypeIcon({type}: {type: IssueArtifact['type']}) {
  if (type === 'plan') return <ChecklistIcon size={14} className={styles.Icon} />
  if (type === 'link') return <LinkExternalIcon size={14} className={styles.Icon} />
  return <FileIcon size={14} className={styles.Icon} />
}

type PersistedArtifactRowProps = {
  id: number
  type: IssueArtifact['type']
  name: string
  body: string
  bodyHTML?: string
  onDelete?: (id: number) => void
  onSelect?: (artifact: PanelArtifact) => void
}

export const PersistedArtifactRow = memo(function PersistedArtifactRowImpl({
  id,
  type,
  name,
  body,
  bodyHTML,
  onDelete,
  onSelect,
}: PersistedArtifactRowProps) {
  const isLink = type === 'link'
  const description =
    !isLink && body
      ? body
          .split('\n')
          .find(l => l.trim())
          ?.trim()
      : undefined
  const truncated = description && description.length > 120 ? `${description.slice(0, 120)}…` : description

  const handleClick = useCallback(() => {
    onSelect?.({
      id: `${type}:${id}`,
      type,
      name,
      body,
      bodyHTML: bodyHTML ?? '',
    })
  }, [id, type, name, body, bodyHTML, onSelect])

  const handleDelete = useCallback(() => {
    onDelete?.(id)
  }, [id, onDelete])

  const linkHref = isLink ? safeHref(body) : undefined

  return (
    <li className={styles.Row}>
      <ArtifactTypeIcon type={type} />
      <div className={styles.Content}>
        {isLink && linkHref ? (
          <Link className={styles.TitleButton} href={linkHref} target="_blank" rel="noopener noreferrer">
            <span className={styles.Title}>{name}</span>
          </Link>
        ) : (
          <button type="button" className={styles.TitleButton} aria-label={name} onClick={handleClick}>
            <span className={styles.Title}>{name}</span>
          </button>
        )}
        {truncated && <span className={styles.Description}>{truncated}</span>}
      </div>
      <div className={styles.HoverActions}>
        <ActionMenu>
          <ActionMenu.Anchor>
            <IconButton icon={KebabHorizontalIcon} variant="invisible" size="small" aria-label="More actions" />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Item variant="danger" onSelect={handleDelete}>
                <ActionList.LeadingVisual>
                  <TrashIcon />
                </ActionList.LeadingVisual>
                Delete
              </ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
    </li>
  )
})
